import { type ChangeEvent, type KeyboardEvent, useCallback, useRef, useState } from "react";
import {
  Button,
  Input,
  Modal,
  RichTextEditor,
  Toast,
  type RichTextMediaItem,
  type RichTextPreset,
  type ToastType,
} from "@legal-500/legal500-design-system";
import styles from "./RichTextField.module.css";

type Props = {
  value: string;
  onChange: (html: string) => void;
  preset: RichTextPreset;
  mediaEnabled?: boolean;
  /**
   * Uploads a locally chosen file and returns the id and the absolute URL of the
   * stored image. Supplying it adds the picker's "Upload" source; without it the
   * picker offers no upload at all.
   */
  uploadImage?: (file: File) => Promise<{ id: string; url: string }>;
  /**
   * When true, the picker gains a "From URL" source that lets staff embed a
   * remote `https://…` image URL directly (no upload). Only enable this for
   * fields whose server-side sanitiser keeps remote images — currently just the
   * user guides. Default false.
   */
  allowImageUrl?: boolean;
  htmlTab?: boolean;
  label?: string;
  description?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  id?: string;
  ariaLabel?: string;
  minHeight?: number;
};

type PendingResolver = (value: RichTextMediaItem | null) => void;

type UploadedImage = {
  id: string;
  url: string;
  filename: string;
  mimeType: string;
};

type PickerTab = "upload" | "url";

const TAB_ORDER: PickerTab[] = ["upload", "url"];

/**
 * Remote image URLs are restricted to `https:` only. The server-side sanitiser
 * is https-only, so an `http:` URL would be stripped on save — reject it here
 * with a clear inline error instead of silently losing the image.
 */
function isValidHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Staff-ui adapter around the design-system `RichTextEditor`.
 *
 * Owns the image-picker promise state machine: when the editor's toolbar image
 * button is pressed (only shown when `mediaEnabled`), we open a modal and
 * resolve a pending `Promise<RichTextMediaItem | null>` once the user confirms
 * (or cancels). Two insertion sources are offered:
 *
 *  - **Upload** (only when `uploadImage` is supplied): pick a local file, hand
 *    it to the caller's uploader and insert the absolute URL it returns.
 *    Optional alt text is kept on the inserted `<img>`.
 *  - **From URL** (only when `allowImageUrl`): paste an `https://…` image URL.
 *    Kept only for fields whose server sanitiser allows remote images.
 *
 * Tabs appear only when both sources are available; with one source the picker
 * shows that source's panel on its own.
 */
export function RichTextField(props: Props) {
  const { mediaEnabled, allowImageUrl, uploadImage } = props;
  const allowUpload = Boolean(uploadImage);
  const defaultTab: PickerTab = allowUpload ? "upload" : "url";
  const showTabs = allowUpload && Boolean(allowImageUrl);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  // ---- Picker promise machinery ---------------------------------------------
  const pendingResolverRef = useRef<PendingResolver | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTabRef = useRef<HTMLButtonElement>(null);
  const urlTabRef = useRef<HTMLButtonElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [tab, setTab] = useState<PickerTab>(defaultTab);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState<UploadedImage | null>(null);
  const [altText, setAltText] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [urlError, setUrlError] = useState<string | null>(null);

  const resolveAndClose = useCallback(
    (item: RichTextMediaItem | null) => {
      const resolver = pendingResolverRef.current;
      pendingResolverRef.current = null;
      setPickerOpen(false);
      setUploaded(null);
      setAltText("");
      setImageUrl("");
      setUrlError(null);
      setUploading(false);
      setTab(defaultTab);
      if (resolver) resolver(item);
    },
    [defaultTab],
  );

  const pickMedia = useCallback((): Promise<RichTextMediaItem | null> => {
    if (!mediaEnabled) return Promise.resolve(null);
    return new Promise<RichTextMediaItem | null>(resolve => {
      pendingResolverRef.current = resolve;
      setUploaded(null);
      setAltText("");
      setImageUrl("");
      setUrlError(null);
      setTab(defaultTab);
      setPickerOpen(true);
    });
  }, [mediaEnabled, defaultTab]);

  const cancel = useCallback(() => resolveAndClose(null), [resolveAndClose]);

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file || !uploadImage) return;
      setUploading(true);
      try {
        const { id, url } = await uploadImage(file);
        setUploaded({ id, url, filename: file.name, mimeType: file.type || "image/*" });
      } catch (err) {
        setToast({
          message: err instanceof Error ? err.message : "Upload failed",
          type: "error",
        });
      } finally {
        setUploading(false);
      }
    },
    [uploadImage],
  );

  const insertUpload = useCallback(() => {
    if (!uploaded) return;
    const trimmed = altText.trim();
    resolveAndClose({
      id: uploaded.id,
      url: uploaded.url,
      altText: trimmed || undefined,
      mimeType: uploaded.mimeType,
    });
  }, [uploaded, altText, resolveAndClose]);

  const insertUrl = useCallback(() => {
    const url = imageUrl.trim();
    if (!isValidHttpUrl(url)) {
      setUrlError("Enter an https:// image URL.");
      return;
    }
    const trimmed = altText.trim();
    resolveAndClose({
      id: url,
      url,
      altText: trimmed || undefined,
    });
  }, [imageUrl, altText, resolveAndClose]);

  const selectTab = useCallback((next: PickerTab) => {
    setTab(next);
    setAltText("");
    setUrlError(null);
  }, []);

  // Roving-tabindex arrow-key navigation for the tablist (WAI-ARIA tabs pattern):
  // Left/Right wrap between tabs, Home/End jump to the ends. Moving selection
  // also moves focus to the newly selected tab.
  const handleTabKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      const current = TAB_ORDER.indexOf(tab);
      let nextIndex: number | null = null;
      switch (e.key) {
        case "ArrowLeft":
          nextIndex = (current - 1 + TAB_ORDER.length) % TAB_ORDER.length;
          break;
        case "ArrowRight":
          nextIndex = (current + 1) % TAB_ORDER.length;
          break;
        case "Home":
          nextIndex = 0;
          break;
        case "End":
          nextIndex = TAB_ORDER.length - 1;
          break;
        default:
          return;
      }
      e.preventDefault();
      const next = TAB_ORDER[nextIndex];
      selectTab(next);
      const ref = next === "upload" ? uploadTabRef : urlTabRef;
      ref.current?.focus();
    },
    [tab, selectTab],
  );

  // ---- onUploadAttempt — surface drop/paste rejection via toast -------------
  const handleUploadAttempt = useCallback((message: string) => {
    setToast({
      message: message || "Use the toolbar image button to insert an image.",
      type: "info",
    });
  }, []);

  const insertDisabled = tab === "upload" ? !uploaded || uploading : imageUrl.trim().length === 0;

  return (
    <>
      <RichTextEditor
        value={props.value}
        onChange={props.onChange}
        preset={props.preset}
        mediaEnabled={mediaEnabled}
        htmlTab={props.htmlTab}
        label={props.label}
        description={props.description}
        required={props.required}
        error={props.error}
        disabled={props.disabled}
        id={props.id}
        ariaLabel={props.ariaLabel}
        minHeight={props.minHeight}
        pickMedia={mediaEnabled ? pickMedia : undefined}
        onUploadAttempt={handleUploadAttempt}
      />

      {mediaEnabled && pickerOpen && (
        <Modal isOpen onClose={cancel} title="Insert image" size="medium" blurBackground>
          <div className={styles.picker}>
            {showTabs && (
              <div className={styles.tabs} role="tablist" aria-label="Image source">
                <button
                  ref={uploadTabRef}
                  type="button"
                  role="tab"
                  id="rtf-tab-upload"
                  aria-selected={tab === "upload"}
                  aria-controls="rtf-panel-upload"
                  tabIndex={tab === "upload" ? 0 : -1}
                  className={`${styles.tab} ${tab === "upload" ? styles.tabActive : ""}`}
                  onClick={() => selectTab("upload")}
                  onKeyDown={handleTabKeyDown}
                >
                  Upload
                </button>
                <button
                  ref={urlTabRef}
                  type="button"
                  role="tab"
                  id="rtf-tab-url"
                  aria-selected={tab === "url"}
                  aria-controls="rtf-panel-url"
                  tabIndex={tab === "url" ? 0 : -1}
                  className={`${styles.tab} ${tab === "url" ? styles.tabActive : ""}`}
                  onClick={() => selectTab("url")}
                  onKeyDown={handleTabKeyDown}
                >
                  From URL
                </button>
              </div>
            )}

            {tab === "upload" && allowUpload && (
              <div
                className={styles.panel}
                role={showTabs ? "tabpanel" : undefined}
                id={showTabs ? "rtf-panel-upload" : undefined}
                aria-labelledby={showTabs ? "rtf-tab-upload" : undefined}
              >
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading
                    ? "Uploading…"
                    : uploaded
                      ? "Choose a different image"
                      : "Choose image…"}
                </Button>
                {uploaded && <p className={styles.hint}>Selected: {uploaded.filename}</p>}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  hidden
                  aria-hidden="true"
                  onChange={handleFileChange}
                />
                {uploaded && (
                  <>
                    <p className={styles.hint}>
                      Describe this image for screen reader users. Leave blank only if the image is
                      purely decorative.
                    </p>
                    <Input
                      label="Alt text"
                      value={altText}
                      onChange={e => setAltText(e.target.value)}
                      autoFocus
                      fullWidth
                    />
                  </>
                )}
              </div>
            )}

            {tab === "url" && allowImageUrl && (
              <div
                className={styles.panel}
                role={showTabs ? "tabpanel" : undefined}
                id={showTabs ? "rtf-panel-url" : undefined}
                aria-labelledby={showTabs ? "rtf-tab-url" : undefined}
              >
                <Input
                  label="Image URL"
                  type="url"
                  inputMode="url"
                  placeholder="https://example.com/image.png"
                  value={imageUrl}
                  onChange={e => {
                    setImageUrl(e.target.value);
                    if (urlError) setUrlError(null);
                  }}
                  error={urlError ?? undefined}
                  fullWidth
                />
                <p className={styles.hint}>
                  Paste a public https:// image URL. Describe the image for screen reader users
                  below.
                </p>
                <Input
                  label="Alt text"
                  value={altText}
                  onChange={e => setAltText(e.target.value)}
                  fullWidth
                />
              </div>
            )}

            <div className={styles.footer}>
              <Button type="button" variant="outlined" size="small" onClick={cancel}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="contained"
                size="small"
                onClick={tab === "upload" ? insertUpload : insertUrl}
                disabled={insertDisabled}
              >
                Insert image
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
}
